import { useEffect } from 'react';
import { useMap, useMapsLibrary } from '@vis.gl/react-google-maps';

// Heatmap component that uses Google Maps visualization library
export function ChlorophyllHeatmap({ points, visible }) {
  const map = useMap();
  const visualization = useMapsLibrary('visualization');
  
  useEffect(() => {
    if (!map || !visualization || !points?.length || !visible) return;
    
    // Convert points to Google Maps LatLng with weight
    // Boost intensity for sparse data scenarios
    const heatmapData = points.map(point => ({
      location: new window.google.maps.LatLng(point.lat, point.lon),
      weight: Math.max((point.intensity || 0.3) * 20, 5)  // Minimum weight of 5
    }));
    
    // Create heatmap layer with settings optimized for sparse data
    const heatmapLayer = new visualization.HeatmapLayer({
      data: heatmapData,
      map: map,
      radius: 80,  // Large radius for visibility
      opacity: 0.75,
      maxIntensity: 8,
      dissipating: true,
      gradient: [
        'rgba(0, 100, 0, 0)',        // Transparent
        'rgba(0, 150, 50, 0.4)',     // Light green
        'rgba(50, 205, 50, 0.5)',    // Lime green
        'rgba(154, 205, 50, 0.6)',   // Yellow-green
        'rgba(255, 215, 0, 0.7)',    // Gold
        'rgba(255, 165, 0, 0.75)',   // Orange
        'rgba(255, 69, 0, 0.8)',     // Red-orange
        'rgba(139, 0, 0, 0.85)'      // Dark red
      ]
    });
    
    return () => {
      heatmapLayer.setMap(null);
    };
  }, [map, visualization, points, visible]);
  
  return null;
}

// Risk level colors and descriptions
export const RISK_CONFIG = {
  low: { 
    color: '#22c55e', 
    bg: 'bg-green-500', 
    bgLight: 'bg-green-50',
    text: 'text-green-700',
    border: 'border-green-200',
    label: 'Lav',
    description: 'Normal - ingen bekymring'
  },
  moderate: { 
    color: '#eab308', 
    bg: 'bg-yellow-500',
    bgLight: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    label: 'Moderat',
    description: 'Forhøyet - overvåk situasjonen'
  },
  high: { 
    color: '#f97316', 
    bg: 'bg-orange-500',
    bgLight: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    label: 'Høy',
    description: 'Høy - mulig algeoppblomstring'
  },
  very_high: { 
    color: '#ef4444', 
    bg: 'bg-red-500',
    bgLight: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Svært høy',
    description: 'Svært høy - sannsynlig oppblomstring'
  },
  extreme: { 
    color: '#7c2d12', 
    bg: 'bg-red-900',
    bgLight: 'bg-red-100',
    text: 'text-red-900',
    border: 'border-red-300',
    label: 'Ekstrem',
    description: 'Ekstrem - aktiv oppblomstring'
  },
  unknown: { 
    color: '#6b7280', 
    bg: 'bg-gray-500',
    bgLight: 'bg-gray-50',
    text: 'text-gray-700',
    border: 'border-gray-200',
    label: 'Ingen data',
    description: 'Satellittdata ikke tilgjengelig'
  }
};

// Norway map center
export const NORWAY_CENTER = { lat: 63.5, lng: 10.5 };